import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, Observer } from 'rxjs'
import { AppLabInfo, EditorAppLabInfo } from '@monorepo/golab-generated'
import { ReportService } from './report.service'

export interface LowercaseDescription extends EditorAppLabInfo {
  lowercaseTitle: string
  lowercaseDescription: string
  lowercaseKeywordList: Array<string>
  lowercaseKeywordString: string
  lowercaseAuthor: string
}

/**
 * This service is also used in the ils-player.
 * The app/lab lists are only loaded on demand to prevent unnecessary loading of the lists.
 */
@Injectable({
  providedIn: 'root',
})
export class AppLabListService<T extends LowercaseDescription> {
  private readonly reportService = inject(ReportService)
  descriptionList: T[] = []
  readonly notSupportedList: AppLabInfo[] = []
  readonly listsLoaded = new BehaviorSubject(false)
  private readonly descriptionUrlMap = new Map<string, T>()
  private readonly notSupportedUrlSet = new Set<string>()
  private descriptionListLoaded = false
  private notSupportedListLoaded = false
  private loadStarted = false

  loadList(): void {
    // Guard on "started", not "loaded": callers (e.g. every content-icon) invoke this before the first response arrives
    if (!this.loadStarted) {
      this.loadStarted = true
      this.startLoadList()
    }
  }

  protected startLoadList() {
    throw new Error('Method not implemented.')
  }

  urlNotSupported(url: string): boolean {
    return this.notSupportedUrlSet.has(url.toLowerCase())
  }

  getDescriptionByUrl(url: string): T | undefined {
    return this.descriptionUrlMap.get(url.toLowerCase())
  }

  protected appLabListSubscriber(): Partial<Observer<EditorAppLabInfo[]>> {
    return {
      next: (data: EditorAppLabInfo[]) => {
        // console.log('data', data)
        const descriptionListWithLowerCaseList: T[] = data.map((editorAppLabInfo) => {
          const lowercaseKeywordList = editorAppLabInfo.keywordList.map((keyword) => keyword.toLowerCase())
          return {
            ...editorAppLabInfo,
            lowercaseTitle: editorAppLabInfo.title.toLowerCase(),
            lowercaseDescription: editorAppLabInfo.shortDescription.toLowerCase(),
            lowercaseKeywordList: lowercaseKeywordList,
            lowercaseKeywordString: lowercaseKeywordList.join(', '),
            lowercaseAuthor: editorAppLabInfo.author.toLowerCase(),
          } as T
        })
        this.parseDescriptionList(descriptionListWithLowerCaseList)
        this.descriptionListLoaded = true
        this.descriptionList.sort((a, b) => a.title.localeCompare(b.title))
        this.updateDescriptionListList()
      },
      error: (error) => {
        console.error('error', error)
        this.reportService.reportError('Retrieving app descriptions', error)
      },
    }
  }

  protected appLabNotSupportedListSubscriber(): Partial<Observer<AppLabInfo[]>> {
    return {
      next: (data: AppLabInfo[]) => {
        // console.log('data', data)
        this.notSupportedList.push(...data)
        data.forEach((appLabInfo) => this.notSupportedUrlSet.add(appLabInfo.embedLink.toLowerCase()))
        this.notSupportedListLoaded = true
        this.updateDescriptionListList()
      },
      error: (error) => {
        console.error('error', error)
        this.reportService.reportError('Retrieving app descriptions', error)
      },
    }
  }

  protected parseDescriptionList(descriptionList: T[]): void {
    this.descriptionList = []
    descriptionList.forEach((appDescription) => {
      const lowerCaseUrl = appDescription.embedLink.toLowerCase()
      this.descriptionUrlMap.set(lowerCaseUrl, appDescription)
      if (appDescription.showInEditor) {
        this.descriptionList.push(appDescription)
      }
    })
  }

  private updateDescriptionListList(): void {
    if (this.descriptionListLoaded && this.notSupportedListLoaded) {
      this.listsLoaded.next(true)
    }
  }
}
